import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import ContentIdeasPage from "./pages/ContentIdeasPage";
import ScriptGeneratorPage from "./pages/ScriptGeneratorPage";
import ContentLibraryPage from "./pages/ContentLibraryPage";
import ExportPage from "./pages/ExportPage";
import VideoWorkspacePage from "./pages/VideoWorkspacePage";
import ServicesPage from "./pages/ServicesPage";
import CalendarPage from "./pages/CalendarPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ProfilePage from "./pages/ProfilePage";
import TemplatesPage from "./pages/TemplatesPage";
import ComplianceHistoryPage from "./pages/ComplianceHistoryPage";
import BatchGenerationPage from "./pages/BatchGenerationPage";
import AdminPage from "./pages/AdminPage";
import { Toaster } from "./components/ui/sonner";
import { supabaseClient } from "./lib/supabaseClient";

function ProtectedRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let isMounted = true;

    if (!supabaseClient) {
      setLoading(false);
      return undefined;
    }

    supabaseClient.auth.getSession().then(({ data }) => {
      if (!isMounted) {
        return;
      }

      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-[#5C7063]">Loading your session...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/:doctorId"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ideas/:doctorId"
            element={
              <ProtectedRoute>
                <ContentIdeasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/script/:doctorId"
            element={
              <ProtectedRoute>
                <ScriptGeneratorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/:doctorId"
            element={
              <ProtectedRoute>
                <ContentLibraryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/export/:doctorId"
            element={
              <ProtectedRoute>
                <ExportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/video/:doctorId"
            element={
              <ProtectedRoute>
                <VideoWorkspacePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/:doctorId"
            element={
              <ProtectedRoute>
                <ServicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/:doctorId"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/:doctorId"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:doctorId"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates/:doctorId"
            element={
              <ProtectedRoute>
                <TemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/batch/:doctorId"
            element={
              <ProtectedRoute>
                <BatchGenerationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compliance/:doctorId"
            element={
              <ProtectedRoute>
                <ComplianceHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
